/**
 * FlickerSkillEffect — Specter wraith-path evolution: replaces Wraith.
 *
 * Longer range (2 tiles), two charges on its own cooldown, and the signature
 * mechanic: Flickering through an enemy resets their aggro to zero. No damage,
 * no debuff — they simply forget you exist. Stealth stays up. Unreliable at
 * first, like any evolution.
 *
 * Dual-resource gate: each activation requires both a Flicker charge (internal,
 * 2 max) AND a dash charge (shared economy via DashComponent). If either is
 * empty, the skill cannot fire. The dash charge cost is the hard gate.
 *
 * Charge recharge: when charges < maxCharges, cooldown ticks down. When it
 * reaches zero, one charge is restored. If still below max, cooldown restarts
 * automatically. All charges full → cooldown stops.
 *
 * Inherits Wraith upgrade hooks through `replacesId: 'wraith'`:
 * - Quickstep (cooldownReduction): fraction reduction on cooldown
 * - Afterimage (afterimageDurationMs, afterimageDrawsInvestigation):
 *   spawns investigation decoy at origin on activation
 *
 * Resolver queries use scope 'wraith' so existing upgrade wiring flows
 * through the replacesId chain without modification.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see specter-drift-path-proposal.md § Flicker
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface FlickerDeps {
  readonly targeting: {
    shouldFizzle(): boolean;
    /** Current player facing direction (normalized). */
    getFacingDirection(): { x: number; y: number };
  };
  readonly dash: {
    /** Current available dash charges. */
    readonly charges: number;
    /** Consume one dash charge without starting a physics dash. */
    consumeCharge(): void;
  };
  readonly movement: {
    /**
     * Reposition the player by an offset in world pixels.
     * This is a teleport-style move — it does not trigger movement-break
     * checks for Hide/Stealth.
     */
    reposition(dx: number, dy: number): void;
  };
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
  };
  readonly afterimage?: {
    /** Spawn an investigation decoy at the given world position. */
    spawnDecoy(x: number, y: number, durationMs: number): void;
    /** Current player world position (for decoy spawn at origin). */
    getCurrentPosition(): { x: number; y: number };
  };
  readonly detection: {
    /**
     * Return enemy IDs whose bodies intersect the line segment from
     * (x1,y1) to (x2,y2). Used to detect pass-through targets.
     */
    getEnemiesAlongPath(x1: number, y1: number, x2: number, y2: number): string[];
    /** Current player world position. */
    getPlayerPosition(): { x: number; y: number };
  };
  readonly ai: {
    /** Reset an enemy's aggro to zero. */
    resetAggro(enemyId: string): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface FlickerConfig {
  readonly cooldownMs: number;
  readonly rangePx: number;
  readonly maxCharges: number;
}

const DEFAULT_CONFIG: FlickerConfig = {
  cooldownMs: 1200,              // medium cooldown tier
  rangePx: 64,                   // ~2 tiles at 32px/tile
  maxCharges: 2,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class FlickerSkillEffect implements SkillEffect {
  readonly skillId = 'flicker';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly failLabel = 'FIZZLE';

  private readonly deps: FlickerDeps;
  private readonly config: FlickerConfig;

  private _charges: number;
  private _cooldownRemaining = 0;
  private _resolvedCooldownMs = 0;
  private _fizzled = false;
  private _activationCount = 0;
  private _lastAggroResetIds: string[] = [];

  constructor(deps: FlickerDeps, config: Partial<FlickerConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
    this._charges = this.config.maxCharges;
  }

  // --- State getters ---

  get charges(): number { return this._charges; }
  get maxCharges(): number { return this.config.maxCharges; }
  get cooldown(): number { return this._cooldownRemaining; }
  get fizzled(): boolean { return this._fizzled; }
  get activationCount(): number { return this._activationCount; }
  /** Enemy IDs whose aggro was reset on the most recent activation. */
  get lastAggroResetIds(): readonly string[] { return this._lastAggroResetIds; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._charges > 0 && this.deps.dash.charges > 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Resolve cooldown: base minus Quickstep reduction (scope: 'wraith' via replacesId)
    const cooldownReduction = this.deps.resolver.getAdditiveEffect(
      'wraith', 'cooldownReduction',
    );
    this._resolvedCooldownMs = this.config.cooldownMs * (1 - cooldownReduction);

    // Fizzle check — consumes charge + cooldown but does not reposition
    this._fizzled = this.deps.targeting.shouldFizzle();

    if (this._fizzled) {
      this._charges--;
      this.deps.dash.consumeCharge();
      if (this._cooldownRemaining <= 0) {
        this._cooldownRemaining = this._resolvedCooldownMs;
      }
      this._lastAggroResetIds = [];
      return false;
    }

    // Resolve Afterimage: spawn investigation decoy at origin before moving
    const hasAfterimage = this.deps.resolver.hasEffect('wraith', 'afterimageDrawsInvestigation');
    if (hasAfterimage && this.deps.afterimage) {
      const afterimageDuration = this.deps.resolver.getAdditiveEffect(
        'wraith', 'afterimageDurationMs',
      );
      const origin = this.deps.afterimage.getCurrentPosition();
      this.deps.afterimage.spawnDecoy(origin.x, origin.y, afterimageDuration);
    }

    // Capture origin for pass-through detection
    const playerPos = this.deps.detection.getPlayerPosition();
    const dir = this.deps.targeting.getFacingDirection();
    const dx = dir.x * this.config.rangePx;
    const dy = dir.y * this.config.rangePx;

    // Consume resources
    this._charges--;
    this.deps.dash.consumeCharge();

    // Reposition
    this.deps.movement.reposition(dx, dy);

    // Detect enemies along the flicker path and reset their aggro
    const destX = playerPos.x + dx;
    const destY = playerPos.y + dy;
    const passedEnemies = this.deps.detection.getEnemiesAlongPath(
      playerPos.x, playerPos.y, destX, destY,
    );
    this._lastAggroResetIds = passedEnemies;
    for (const enemyId of passedEnemies) {
      this.deps.ai.resetAggro(enemyId);
    }

    // Start cooldown if not already ticking (charges were full before this use)
    if (this._cooldownRemaining <= 0) {
      this._cooldownRemaining = this._resolvedCooldownMs;
    }

    this._activationCount++;
    return true;
  }

  deactivate(): void {
    // Instant skill — no toggle state
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    // Recharge charges via cooldown
    if (this._charges < this.config.maxCharges && this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
      if (this._cooldownRemaining <= 0) {
        this._charges++;
        // If still below max, restart cooldown for next charge
        if (this._charges < this.config.maxCharges) {
          // Resolve cooldown fresh in case upgrade state changed
          const cooldownReduction = this.deps.resolver.getAdditiveEffect(
            'wraith', 'cooldownReduction',
          );
          this._cooldownRemaining = this.config.cooldownMs * (1 - cooldownReduction);
        }
      }
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
    this._charges = 0;
  }
}
